import React, { useEffect, useState } from 'react';
import { isVersionOlder, FRONTEND_VERSION } from '@/lib/version';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { WhatsNewModal } from './WhatsNewModal';

interface ConfigResponse {
    minVersion: string;
    androidStoreUrl: string;
    iosStoreUrl: string;
}

export function ForceUpdateManager({ children }: { children: React.ReactNode }) {
    const [needsUpdate, setNeedsUpdate] = useState<boolean>(false);
    const [storeUrl, setStoreUrl] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { t } = useTranslation();
    const [location] = useLocation();
    const isAdminRoute = location.startsWith('/admin');

    useEffect(() => {
        async function checkVersion() {
            try {
                const response = await apiRequest('GET', '/api/config');
                if (!response.ok) {
                    throw new Error('Failed to fetch config');
                }

                const config: ConfigResponse = await response.json();
                const currentVersion = FRONTEND_VERSION;

                if (isVersionOlder(currentVersion, config.minVersion)) {
                    // Determine the correct store URL based on platform
                    const platform = Capacitor.getPlatform();
                    if (platform === 'ios') {
                        setNeedsUpdate(true);
                        setStoreUrl(config.iosStoreUrl);
                    } else if (platform === 'android') {
                        setNeedsUpdate(true);
                        setStoreUrl(config.androidStoreUrl);
                    }
                    // Pour la version web, on ne force pas la mise à jour
                }
            } catch (error) {
                console.error("Error checking app version:", error);
            } finally {
                setIsLoading(false);
            }
        }

        checkVersion();
    }, []);

    const handleUpdateClick = async () => {
        if (storeUrl) {
            if (Capacitor.isNativePlatform()) {
                try {
                    // Attempt to use Capacitor App plugin to open URL if possible, otherwise rely on window.location
                    window.location.href = storeUrl;
                } catch (e) {
                    window.open(storeUrl, '_system');
                }
            } else {
                window.open(storeUrl, '_blank');
            }
        }
    };

    // If loading, you can choose to return null to avoid flash, but here we just render children
    // and overlay the update screen when needed.
    if (needsUpdate && !isAdminRoute) {
        return (
            <div className="fixed inset-0 z-[9999] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-card shadow-2xl rounded-2xl p-8 max-w-sm w-full border text-center space-y-6 animate-in fade-in zoom-in duration-300">
                    <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mb-4">
                        <ShieldAlert size={32} />
                    </div>

                    <h2 className="text-2xl font-bold tracking-tight text-foreground">
                        {t('force_update.title', 'Mise à jour obligatoire')}
                    </h2>

                    <p className="text-muted-foreground text-sm leading-relaxed">
                        {t('force_update.description', 'Pour garantir votre sécurité et vous offrir la meilleure expérience possible, vous devez installer la dernière version de PadELO.')}
                    </p>

                    <button
                        onClick={handleUpdateClick}
                        className="w-full relative overflow-hidden bg-padel-green hover:bg-padel-green-light text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-padel-green focus:ring-offset-2 active:scale-95"
                    >
                        {t('force_update.button', 'Mettre à jour maintenant')}
                    </button>
                </div>
            </div>
        );
    }

    return (
      <>
        <WhatsNewModal />
        {children}
      </>
    );
}
